import { useState } from 'react';
import { toStringDate, useInputDefinition } from '@gov-nx/utils/common';
import { usePPPLocale } from '../..';
import { useFormBuilder } from '@gov-nx/core/service';
import type {
	SearchFormData,
	Fields,
	SearchFormReturnType,
	ModalFields,
	ModalFormData,
	ModalFormReturnType,
	UseModalFormProps,
} from './types/formDefinition/formDefinition.types';
import { usePPPSeznamFilter } from './hooks/usePPPSeznamFilter';
import { resolveModalFormStrings } from './utils';
import {
	useDateRangeFormDefinition,
	useRezaChangeOptions,
} from '@gov-nx/core/hooks';
import { REZA_PPP_FORM_PERMISSIONS } from './constants';

export const useSearchForm = (): SearchFormReturnType => {
	const { t } = usePPPLocale();
	const { filter, onSearchTextChange } = usePPPSeznamFilter();

	const text = useInputDefinition({
		name: 'text',
		label: '',
		placeholder: t('seznam.sluzby.formular.search.placeholder'),
		defaultValue: filter.text,
	});

	const formDefinition = useFormBuilder<SearchFormData, Fields>([text]);

	const onSubmit = formDefinition.formMethods.handleSubmit((data) =>
		onSearchTextChange(data.text)
	);

	return {
		formDefinition,
		onSubmit,
	};
};

export const useModalForm = ({
	isOpen,
	activeTab,
	activeSwitcherReza,
	setActiveSwitcherReza,
}: UseModalFormProps): ModalFormReturnType => {
	const { t } = usePPPLocale();
	const { filter, onModalFiltersChange, onFilterParamsReset } =
		usePPPSeznamFilter();
	const strings = resolveModalFormStrings(activeTab, t);

	const rezaOptionsData = useRezaChangeOptions({
		permissions: REZA_PPP_FORM_PERMISSIONS,
	});

	const [pendingReza, setPendingReza] = useState(activeSwitcherReza);

	const dateRangeFields = useDateRangeFormDefinition({
		fields: {
			from: {
				label: t('seznam.sluzby.formular.modal.datumOd.label'),
				defaultValue: filter.datumOd,
			},
			to: {
				label: t('seznam.sluzby.formular.modal.datumDo.label'),
				defaultValue: filter.datumDo,
			},
		},
	});

	const formDefinition = useFormBuilder<ModalFormData, ModalFields>([
		...dateRangeFields,
	]);

	const onSubmit = formDefinition.formMethods.handleSubmit((data) => {
		const { datumOd, datumDo } = data;

		onModalFiltersChange({
			datumOd: datumOd ? toStringDate(datumOd) : undefined,
			datumDo: datumDo ? toStringDate(datumDo) : undefined,
		});
		setActiveSwitcherReza(pendingReza);
		isOpen.setFalse();
	});

	const onFilterReset = () => {
		onFilterParamsReset();
		setActiveSwitcherReza(null);
		isOpen.setFalse();
	};

	return {
		formDefinition,
		onSubmit,
		onFilterReset,
		isOpen,
		rezaOptionsData,
		setPendingReza,
		strings: {
			label: strings.label,
			description: strings.description,
			button: {
				submit: {
					text: t('seznam.sluzby.formular.modal.button.submit.text'),
					wgac: t('seznam.sluzby.formular.modal.button.submit.wgac'),
				},
				reset: {
					text: t('seznam.sluzby.formular.modal.button.reset.text'),
					wgac: t('seznam.sluzby.formular.modal.button.reset.wgac'),
				},
			},
		},
	};
};
